import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Lock, Globe, Plus, ChevronRight } from 'lucide-react';

import { fetchRecommendedGroupsForStock } from '../../../redux/userGroups/groupsToStock/groupsToStockActions';
import {
  selectRecommendedGroupsForTicker,
  selectRecommendedGroupsForTickerStatus,
  selectRecommendedStockForTicker,
} from '../../../redux/userGroups/groupsToStock/groupsToStocksSlice';
import GroupCredibilityChip from '../../Groups/groupPageComponents/GroupCredibilityChip';
import { resolveMediaUrl } from '../../../utils/resolveMediaUrl';

const AvatarStack = ({ users = [] }) => (
  <div className="flex items-center -space-x-2">
    {users.map((u) => (
      <div
        key={u.id}
        title={u.username}
        className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 border-2 border-white dark:border-slate-900 flex items-center justify-center flex-shrink-0"
      >
        {u.profile_picture ? (
          <img src={resolveMediaUrl(u.profile_picture)} alt={u.username} className="w-full h-full rounded-full object-cover" />
        ) : (
          <span className="text-[9px] font-semibold text-white">{(u.username || '?').charAt(0).toUpperCase()}</span>
        )}
      </div>
    ))}
  </div>
);

const GroupCard = ({ group, onClick }) => {
  const { t } = useTranslation();
  return (
  <button
    onClick={onClick}
    className="w-full text-left p-4 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-indigo-400/50 dark:border-slate-600 dark:bg-slate-800/60 dark:hover:bg-slate-800 dark:hover:border-indigo-500/50 transition-all duration-300 group"
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow">
        <span className="text-sm font-bold text-white">{group.name?.slice(0, 2).toUpperCase() || 'GR'}</span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-300 transition-colors">
            {group.name}
          </h4>
          {group.privacy === 'private' ? (
            <Lock className="w-3 h-3 text-amber-700 dark:text-amber-400 flex-shrink-0" />
          ) : (
            <Globe className="w-3 h-3 text-emerald-700 dark:text-emerald-400 flex-shrink-0" />
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{group.description}</p>

        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Users className="w-3 h-3" />
            <span>{group.members?.length ?? 0}</span>
          </div>
          {group.credibility && <GroupCredibilityChip credibility={group.credibility} compact />}
          {group.followed_members_count > 0 && (
            <div className="flex items-center gap-1.5">
              <AvatarStack users={group.followed_member_previews} />
              <span className="text-xs text-indigo-600 dark:text-indigo-300 font-medium">
                {t('stocks.groupsPanel.followedHere', {
                  count: group.followed_members_count,
                  verb: group.followed_members_count === 1 ? 'is' : 'are',
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 dark:text-gray-500 dark:group-hover:text-indigo-400 flex-shrink-0 mt-1 transition-colors" />
    </div>
  </button>
  );
};

const StockGroupsPanel = ({ symbol }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((s) => s.auth?.user?.id);

  const groups = useSelector((s) => selectRecommendedGroupsForTicker(s, symbol));
  const status = useSelector((s) => selectRecommendedGroupsForTickerStatus(s, symbol));
  const stock = useSelector((s) => selectRecommendedStockForTicker(s, symbol));

  useEffect(() => {
    if (symbol && userId) {
      dispatch(fetchRecommendedGroupsForStock(symbol));
    }
  }, [dispatch, symbol, userId]);

  const handleCreateGroup = () => {
    navigate('/create-group', {
      state: stock ? { prefillStock: { id: stock.id, symbol: stock.symbol, name: stock.Name } } : undefined,
    });
  };

  // Not logged in -- the endpoint is auth-required (it's inherently
  // personalized), so quietly render nothing rather than a broken fetch.
  if (!userId) return null;

  return (
    // Violet/indigo/blue gradient chrome, matching StockHeader.jsx's hero
    // treatment (paired light/dark, same as that file) -- groups are the
    // app's primary revenue surface, so this panel is styled as an equally
    // first-class, on-brand sibling rather than a lighter, secondary card.
    <section className="bg-white border border-gray-200 dark:bg-slate-900 dark:border-slate-700 rounded-2xl p-6 shadow-xl">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <span className="text-gray-900 dark:text-white">{t('stocks.groupsPanel.heading')}</span>
          <span className="bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 dark:from-violet-400 dark:via-indigo-300 dark:to-blue-400 bg-clip-text text-transparent">
            ${symbol}
          </span>
        </h3>
        {groups.length > 0 && (
          <button
            onClick={handleCreateGroup}
            className="text-xs font-medium text-indigo-700 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200 flex items-center gap-1 transition-colors bg-indigo-100 dark:bg-indigo-500/10 border border-indigo-500/20 rounded-full px-3 py-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('stocks.groupsPanel.newGroup')}
          </button>
        )}
      </div>

      {status === 'loading' && groups.length === 0 && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 rounded-full border-2 border-indigo-500/30 border-t-indigo-500 animate-spin" />
        </div>
      )}

      {status !== 'loading' && groups.length === 0 && (
        <div className="text-center py-8 px-4">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-indigo-100 dark:bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
            <Users className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
          </div>
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {t('stocks.groupsPanel.emptyTitle', { symbol })}
          </p>
          <p className="text-xs text-gray-500 mt-1 mb-4">
            {t('stocks.groupsPanel.emptySubtitle')}
          </p>
          <button
            onClick={handleCreateGroup}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-blue-600 hover:from-violet-400 hover:to-blue-500 shadow-lg shadow-violet-500/20 transition-all"
          >
            <Plus className="w-4 h-4" />
            {t('stocks.groupsPanel.createForSymbol', { symbol })}
          </button>
        </div>
      )}

      {groups.length > 0 && (
        <div className="space-y-2.5">
          {groups.map((group) => (
            <GroupCard key={group.id} group={group} onClick={() => navigate(`/groups/${group.id}`)} />
          ))}
        </div>
      )}
    </section>
  );
};

export default StockGroupsPanel;
